import { useQuery } from "@tanstack/react-query";
import { ReactComponent as OutlineSearch } from "assets/svg/outline-search.svg";
import ConfirmModal from "components/confirm-modal";
import CustomSelectionControl from "components/custom-selection-control/CustomSelectionControl";
import ModalLayout, {
  ModalLayoutProps,
} from "components/modal/modal-layout/ModalLayout";
import useToggleModal from "hooks/useToggleModal";
import messages from "messages";
import { listSchemasQueryOptions } from "queries/schemaQueryOptions";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, FormControl, InputGroup } from "react-bootstrap";
import getClasses from "utils/getClasses";
import { openTabAndPassData } from "utils/newTab";
import styles from "./NoteTemplateSelector.module.scss";

const openPreview = (arg: NoteSchema) => {
  openTabAndPassData("/pro-dashboard/notes-and-forms/view-note-template", arg);
};

interface NoteTemplateSelectorProps {
  defaultSchemaId?: number;
  onApply: (arg: NoteSchema) => Promise<void>;
  labelModalHeader: string;
  labelCancel?: string;
  labelApply: string;
  /**
   * info string to be show on the top of the modal body
   */
  labelInfo: string;
  show: ModalLayoutProps["show"];
  onHide: NonNullable<ModalLayoutProps["onHide"]>;
  requireConfirmation?: boolean;
  open?: () => void;
}

/**
 * renders a button and modal to choose the template
 */
const NoteTemplateSelector = ({
  defaultSchemaId,
  onApply,
  labelModalHeader,
  labelCancel,
  labelApply,
  labelInfo,
  show,
  onHide,
  open,
  requireConfirmation,
}: NoteTemplateSelectorProps) => {
  const noteSchemasQuery = useQuery(listSchemasQueryOptions("note"));
  const noteSchemas = useMemo(
    () => noteSchemasQuery.data ?? [],
    [noteSchemasQuery.data],
  );
  const [noteSchema, setNoteSchema] = useState<NoteSchema>();

  useEffect(() => {
    setNoteSchema(
      noteSchemas.find((el) => el.note_schema_id === defaultSchemaId),
    );
  }, [defaultSchemaId, noteSchemas]);

  const [search, setSearch] = useState("");
  const [error, setError] = useState<any>();
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    if (!search) {
      return noteSchemas;
    }

    return noteSchemas.filter((el) =>
      el.title?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [noteSchemas, search]);

  const handleApply = useCallback(async () => {
    setError(undefined);
    setBusy(false);

    if (noteSchema) {
      setBusy(true);

      try {
        await onApply(noteSchema);
        setError(undefined);
        onHide();
      } catch (error) {
        setError(error);
      } finally {
        setBusy(false);
      }
    } else {
      setBusy(false);
      setError(undefined);
      onHide();
    }
  }, [onHide, noteSchema, onApply]);

  // reset to default form
  const reset = useCallback(() => {
    setNoteSchema(
      noteSchemas.find((el) => el.note_schema_id === defaultSchemaId),
    );
  }, [defaultSchemaId, noteSchemas]);

  const handleHide = useCallback(() => {
    reset();
    setError(undefined);
    onHide();
  }, [onHide, reset]);

  const {
    show: showConfirm,
    open: openConfirm,
    close: closeConfirm,
  } = useToggleModal(false);

  return (
    <>
      <ConfirmModal
        labelTitle={"Switch note template?"}
        children={
          "If you switch to a different template, any edits made to the current template will be deleted."
        }
        labelCancel={messages.waitTakeMeBack}
        labelConfirm={messages.yesContinue}
        show={showConfirm}
        onCancel={() => {
          closeConfirm();
          open && open();
        }}
        onConfirm={() => {
          closeConfirm();
          handleApply();
        }}
      />
      <ModalLayout
        showHeaderDivider={true}
        disabled={busy}
        error={error}
        show={show}
        onHide={handleHide}
        Header={labelModalHeader}
        Footer={
          <>
            <Button
              variant={"link"}
              className={styles["note-template-selector__cancel"]}
              onClick={handleHide}
              disabled={busy}
            >
              {labelCancel || messages.cancel}
            </Button>
            <Button
              type={"button"}
              className={styles["note-template-selector__apply"]}
              onClick={() => {
                requireConfirmation ? handleApply() : openConfirm();
              }}
              disabled={busy || !noteSchema}
            >
              {labelApply}
            </Button>
          </>
        }
        bodyClass={styles["note-template-selector__modal-body"]}
        footerClass={styles["note-template-selector__modal-footer"]}
        headerClass={styles["note-template-selector__modal-header"]}
      >
        <div>
          <div>{labelInfo}</div>
          <InputGroup className={styles["note-template-selector__input-group"]}>
            <InputGroup.Text
              className={styles["note-template-selector__search"]}
            >
              <OutlineSearch />
            </InputGroup.Text>
            <FormControl
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={messages.search}
              className={styles["note-template-selector__input"]}
            />
          </InputGroup>
          <ul className={styles["note-template-selector__list"]}>
            {filtered.map((el) => (
              <li
                key={el.note_schema_id}
                className={getClasses(
                  styles["note-template-selector__list-item"],
                  el === noteSchema &&
                    styles["note-template-selector__list-item--checked"],
                )}
                onClick={() => {
                  setNoteSchema(el);
                  setError(undefined);
                  setBusy(false);
                }}
              >
                <div className={styles["note-template-selector__list-col"]}>
                  <CustomSelectionControl
                    type={"radio"}
                    checked={el === noteSchema}
                  />
                </div>
                <div className={styles["note-template-selector__list-col"]}>
                  {el.title}
                </div>
                <div className={styles["note-template-selector__list-col"]}>
                  <Button
                    variant="link"
                    onClick={(e) => {
                      e.stopPropagation();
                      openPreview(el);
                    }}
                  >
                    {messages.preview}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </ModalLayout>
    </>
  );
};

export default NoteTemplateSelector;
